import React from 'react';

interface CardProps {
  title: string;
  leftValue?: number | string;
  leftDelta?: number | string;
  leftDirection?: 'up' | 'down' | string;
  leftLabel?: string;
  rightValue?: number | string;
  rightDelta?: number | string;
  rightDirection?: 'up' | 'down' | string;
  rightLabel?: string;
  // For automatic calculation
  compareToLeftValue?: number | string;
  compareToRightValue?: number | string;
}

const Card: React.FC<CardProps> = ({
  title,
  leftValue,
  leftDelta,
  leftDirection,
  leftLabel,
  rightValue,
  rightDelta,
  rightDirection,
  rightLabel,
  compareToLeftValue,
  compareToRightValue
}) => {
  // Helper to check if a string is empty or just whitespace
  const isEmpty = (value: any): boolean => {
    if (value === undefined || value === null) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    return false;
  };

  // Convert string value to number for comparison
  const parseValue = (value: string | number | undefined): number => {
    if (isEmpty(value)) return 0;
    if (typeof value === 'number') return value;
    
    // Remove K, M and commas for parsing
    const cleanValue = String(value).replace(/[KM,]/g, '');
    return parseFloat(cleanValue);
  };

  // Calculate direction and delta if not provided
  const calculateDelta = (current: string | number | undefined, compareTo: string | number | undefined) => {
    if (isEmpty(compareTo) || isEmpty(current)) return null;
    
    const currentValue = parseValue(current);
    const compareValue = parseValue(compareTo);
    
    // If values are equal or we can't properly compare
    if (currentValue === compareValue || isNaN(currentValue) || isNaN(compareValue)) return null;
    
    const diff = currentValue - compareValue; // Current - Previous
    const direction = diff > 0 ? 'up' : 'down';
    
    // Format the difference with the appropriate suffix (K or M)
    let formattedDiff = Math.abs(diff).toString();
    if (String(current).includes('K') || String(compareTo).includes('K')) {
      formattedDiff = (Math.abs(diff) / 1000).toFixed(1) + 'K';
    } else if (String(current).includes('M') || String(compareTo).includes('M')) {
      formattedDiff = (Math.abs(diff) / 1000000).toFixed(1) + 'M';
    }
    
    return { delta: formattedDiff, direction };
  };

  // If deltas aren't provided but comparison values are, calculate them
  const leftCalculated = isEmpty(leftDelta) && !isEmpty(compareToLeftValue) 
    ? calculateDelta(leftValue, compareToLeftValue) 
    : null;
    
  const rightCalculated = isEmpty(rightDelta) && !isEmpty(compareToRightValue)
    ? calculateDelta(rightValue, compareToRightValue)
    : null;
    
  // Use calculated or provided values
  const finalLeftDelta = leftCalculated ? leftCalculated.delta : leftDelta;
  const finalLeftDirection = leftCalculated ? leftCalculated.direction : leftDirection;
  
  const finalRightDelta = rightCalculated ? rightCalculated.delta : rightDelta;
  const finalRightDirection = rightCalculated ? rightCalculated.direction : rightDirection;

  // Format delta with proper sign and color
  // In inventory context:
  // NEGATIVE values = GREEN (show with minus sign)
  // POSITIVE values = RED (show with plus sign)
  const formatDelta = (delta: number | string | undefined, direction: string | undefined) => {
    if (isEmpty(delta)) return null;
    
    let numericDelta: number;
    let formattedDelta: string;
    
    // Convert delta to numeric value if it's a string
    if (typeof delta === 'string') {
      // If delta already has a sign, parse it as is
      if (delta.startsWith('-')) {
        numericDelta = -1 * parseValue(delta.substring(1));
        formattedDelta = delta;
      } else if (delta.startsWith('+')) {
        numericDelta = parseValue(delta.substring(1));
        formattedDelta = delta;
      } else {
        // If no sign, use the direction to determine sign
        numericDelta = direction === 'down' ? -1 * parseValue(delta) : parseValue(delta);
        formattedDelta = (numericDelta < 0 ? '-' : '+') + delta;
      }
    } else if (typeof delta === 'number') {
      numericDelta = delta;
      formattedDelta = delta.toString();
    } else {
      return null;
    }
    
    // For inventory: Negative is GREEN, Positive is RED
    const color = numericDelta < 0 ? 'text-[#86BC25]' : 'text-red-600';
    
    // Arrow direction determined by value: DOWN for negative, UP for positive
    // const arrow = numericDelta < 0 ? '↓' : '↑';
    
    // Format delta with sign and parentheses
    return (
      <span className={`${color} font-semibold`}>
        ({formattedDelta} )
      </span>
    );
  };

  return (
    <div className="bg-white shadow-module-card p-[0.6vw] h-full rounded border-[#BEBEBE] border flex flex-col justify-between">
      <h3 className="info-card-title font-semibold mb-2 whitespace-nowrap overflow-hidden text-ellipsis">{title}</h3>
      
      <div className="flex justify-between">
        {/* Left side - OnHand (Qty.) */}
        <div className="flex flex-col items-start w-1/2">
          <p className="flex items-center">
            <span className="info-card-title-value font-normal leading-[1.2] text-deloitteGreen">{leftValue}</span>
          </p>
          <div className="h-6">
            {!isEmpty(finalLeftDelta) && (
              <p>{formatDelta(finalLeftDelta, finalLeftDirection)}</p>
            )}
          </div>
          <p className="info-card-title">{leftLabel}</p>
        </div>
        
        {/* Right side - OnHand ($) */}
        <div className="flex flex-col ml-2 items-start w-1/2">
          <p className="flex items-start">
            <span className="info-card-title-value leading-[1.2] text-deloitteGreen">{rightValue}</span>
          </p>
          <div className="h-6">
            {!isEmpty(finalRightDelta) && (
              <p>{formatDelta(finalRightDelta, finalRightDirection)}</p>
            )}
          </div>
          <p className="info-card-title">{rightLabel}</p>
        </div>
      </div>
    </div>
  );
};

export default Card;
